import { last } from 'lodash';
import { API } from '@shared/types/api';
import { FileUpload } from '@shared/types/fieldValues';

/**
 * The text and media layout items, as the backend defines them.
 *
 * These were modelled by hand ahead of the backend; the shapes below are now generated from the
 * fragments in `api/fragments/viewTemplate`, and the accessors are what absorbed the difference
 * between the guess and the real thing:
 *
 * - an image item holds a list of files, as `images`
 * - a video item holds exactly one video, flattened onto the item rather than held in a list,
 *   with its still in `metadata.thumbnailUrl`
 *
 * Everything outside this file reads content through the accessors and the type guards, so the
 * one-or-many difference between the two media kinds stays here rather than in every renderer.
 */

/**
 * The generated fragments, with `type` pinned to the one value each can hold.
 *
 * Codegen types every item's `type` as the whole `ViewTemplateLayoutItemType` enum, because that
 * is what the schema says. Pinning it restores the discriminated union the hand-written shapes
 * had, which is what lets the guards below narrow and the builder's switches stay exhaustive.
 */
export type ViewTemplateLayoutText = API.ViewTemplateLayoutTextFragment & { type: 'TEXT' };
export type ViewTemplateLayoutImage = API.ViewTemplateLayoutImageFragment & { type: 'IMAGE' };
export type ViewTemplateLayoutVideo = API.ViewTemplateLayoutVideoFragment & { type: 'VIDEO' };

/** One of an image item's files. */
export type LayoutImageFile = NonNullable<ViewTemplateLayoutImage['images']>[number];

/** The two media kinds. They no longer share a shape — only the guards and accessors below. */
export type ViewTemplateLayoutMediaItem = ViewTemplateLayoutImage | ViewTemplateLayoutVideo;

export type ViewTemplateLayoutContentItem =
  | ViewTemplateLayoutText
  | ViewTemplateLayoutImage
  | ViewTemplateLayoutVideo;

/** Every kind a layout can hold. */
export type AnyLayoutItem = API.ViewTemplateLayoutFragment['items'][number];

/** The subset of `LayoutItemType` the content items use. */
export type LayoutContentItemType = ViewTemplateLayoutContentItem['type'];

export function isLayoutTextItem(item: AnyLayoutItem): item is ViewTemplateLayoutText {
  return item.type === 'TEXT';
}

export function isLayoutImageItem(item: AnyLayoutItem): item is ViewTemplateLayoutImage {
  return item.type === 'IMAGE';
}

export function isLayoutVideoItem(item: AnyLayoutItem): item is ViewTemplateLayoutVideo {
  return item.type === 'VIDEO';
}

export function isLayoutMediaItem(item: AnyLayoutItem): item is ViewTemplateLayoutMediaItem {
  return isLayoutImageItem(item) || isLayoutVideoItem(item);
}

export function isLayoutContentItem(item: AnyLayoutItem): item is ViewTemplateLayoutContentItem {
  return isLayoutTextItem(item) || isLayoutMediaItem(item);
}

/* -------------------------------------------------------------------------- */
/*                                  Accessors                                  */
/* -------------------------------------------------------------------------- */
/* The only sanctioned way to read and write content on a layout item. Renderers and the builder
   go through these, so a shape change is a change here rather than everywhere. */

/** The markdown source of a text item, or undefined when it holds nothing worth rendering. */
export function getLayoutText(item: ViewTemplateLayoutText): string | undefined {
  const text = item.text?.trim();
  return text ? text : undefined;
}

/**
 * How long a text layout item may be.
 *
 * The backend is what enforces it, by refusing the save; this is the same number, held here so
 * the composer can count towards it while the instruction is being written.
 */
export const LAYOUT_TEXT_MAX_LENGTH = 8_000;

/**
 * What each media kind accepts. Deliberately a list of exact types rather than `image/*` or
 * `video/*`: those admit plenty a browser will not render — TIFF being the obvious one — and a
 * file that cannot be shown is worse than a file that was refused.
 */
const LAYOUT_IMAGE_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
];
const LAYOUT_VIDEO_CONTENT_TYPES = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];

export function getLayoutMediaContentTypes(item: ViewTemplateLayoutMediaItem): string[] {
  return isLayoutVideoItem(item) ? LAYOUT_VIDEO_CONTENT_TYPES : LAYOUT_IMAGE_CONTENT_TYPES;
}

/**
 * The media an item holds, in order, as uploads. Empty when it holds none, and at most one entry
 * for a video.
 *
 * One accessor for both kinds, so a renderer reading the files is the same call either way — it
 * still branches on the kind to decide between a picture and a player. `FileUpload` is what both
 * the file field and the uploader already speak, and its `posterUrl` is where a video's
 * `metadata.thumbnailUrl` lands.
 */
export function getLayoutMediaFiles(item: ViewTemplateLayoutMediaItem): FileUpload[] {
  if (isLayoutVideoItem(item)) {
    return item.url
      ? [
          {
            url: item.url,
            name: item.name ?? '',
            contentType: item.contentType ?? '',
            posterUrl: item.metadata?.thumbnailUrl ?? undefined,
          },
        ]
      : [];
  }

  return (item.images ?? []).map(toFileUpload);
}

/**
 * Replaces the media on an item with `files`, in that kind's own shape.
 *
 * A video keeps the last of them and drops the rest: the item has room for one, and taking the
 * last is what makes a second upload read as replacing the first.
 */
export function setLayoutMediaFiles(
  item: ViewTemplateLayoutMediaItem,
  files: FileUpload[],
): ViewTemplateLayoutMediaItem {
  if (isLayoutVideoItem(item)) {
    const video = last(files);
    return {
      ...item,
      url: video?.url ?? null,
      name: video?.name ?? null,
      contentType: video?.contentType ?? null,
      metadata: { thumbnailUrl: video?.posterUrl ?? null },
    };
  }

  return { ...item, images: files.map(toImageFile) };
}

/** `LayoutImageFile` allows a null dimension where `FileUpload` wants it absent. */
function toFileUpload(file: LayoutImageFile): FileUpload {
  return {
    url: file.url,
    name: file.name,
    contentType: file.contentType,
    width: file.width ?? undefined,
    height: file.height ?? undefined,
  };
}

/** The reverse, dropping `posterUrl` — an image has no still standing in for it. */
function toImageFile(file: FileUpload): LayoutImageFile {
  return {
    url: file.url,
    name: file.name,
    contentType: file.contentType,
    width: file.width ?? null,
    height: file.height ?? null,
  };
}

/**
 * A video's still as a file in its own right, or undefined where it has none.
 *
 * It borrows the video's name so a tooltip still reads right, but not its dimensions: the item
 * carries none, so an image renders once the browser has measured it.
 */
export function toPosterUpload(file: FileUpload): FileUpload | undefined {
  return file.posterUrl
    ? { url: file.posterUrl, name: file.name, contentType: 'image/jpeg' }
    : undefined;
}

/** False where an item holds nothing yet, and so has nothing to render. */
export function hasLayoutContent(item: ViewTemplateLayoutContentItem): boolean {
  return isLayoutTextItem(item) ? !!getLayoutText(item) : getLayoutMediaFiles(item).length > 0;
}
